import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useCallback, useEffect, useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { NetworkInterfaceSelect } from '@/components/selectors/networkInterfaceSelect';
import { ResourceGroupSelect } from '@/components/selectors/resourceGroupSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { Icon } from '@/components/shared/icon';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { VirtualMachineSizeSection } from './virtualMachineSizeSection';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreAzureVMState } from '../restoreAzureVMWizard';

export const AzureVmConfiguration = (props: StepProps<RestoreAzureVMState>) => {
  const [isNameValid, setIsNameValid] = useState(false);
  const { RestoreVmWithNoNic } = useFeatureFlags();
  const [isOriginalVmCompatible, setIsOriginalVmCompatible] = useState<
    boolean | undefined
  >(undefined);
  const setVMName = (vmName: string, isValid: boolean) => {
    props.setWizardState((state) => ({ ...state, vmName }));
    setIsNameValid(isValid);
  };
  const onKeepOriginalTagsChange = (keepOriginalTags: boolean) =>
    props.setWizardState((state) => ({ ...state, keepOriginalTags }));
  const onTagsChange = (tags: Record<string, string>) =>
    props.setWizardState((state) => ({ ...state, tags }));

  const onCompatibilityChange = useCallback(
    (compatible: boolean | undefined) => {
      setIsOriginalVmCompatible(compatible);
    },
    []
  );

  const onNextClick = async () => {
    if (isOriginalVmCompatible === false) {
      props.setWizardState((state) => ({
        ...state,
        keepOriginalVMSize: false,
      }));
    }
    props.setNextStep(props.currentStep.next?.[0]);
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      onBackClick={props.back}
      canGoNext={() =>
        isNameValid &&
        props.wizardState.vmName !== undefined &&
        props.wizardState.resourceGroup !== undefined &&
        props.wizardState.location !== undefined &&
        (RestoreVmWithNoNic ||
          props.wizardState.networkInterface !== undefined) &&
        (isOriginalVmCompatible === false
          ? Boolean(props.wizardState.vmSize)
          : props.wizardState.keepOriginalVMSize ||
            Boolean(props.wizardState.vmSize)) &&
        isOriginalVmCompatible !== undefined
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <VmName
            cloudAccountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            vmName={props.wizardState.vmName ?? ''}
            onChange={setVMName}
          />
          <RestoreRegionSelect
            isRequired
            restoreAccountId={props.wizardState.restoreAccount?.id}
            cloudProvider={Provider.Azure}
            regionName={props.wizardState.location}
            onChange={(location) =>
              props.setWizardState((state) => ({ ...state, location }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <ResourceGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            onChange={(resourceGroup) =>
              props.setWizardState((state) => ({
                ...state,
                resourceGroup,
              }))
            }
          />
          <NetworkInterfaceSelect
            accountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            networkInterface={props.wizardState.networkInterface}
            location={props.wizardState.location}
            onChange={(networkInterface) =>
              props.setWizardState((state) => ({
                ...state,
                networkInterface,
              }))
            }
          />
        </Stack>
        <VirtualMachineSizeSection
          accountId={props.wizardState.restoreAccount?.id}
          location={props.wizardState.location}
          originalVMSize={props.wizardState.originalVMSize}
          vmSize={props.wizardState.vmSize}
          keepOriginalVMSize={props.wizardState.keepOriginalVMSize}
          disksState={props.wizardState.disksState}
          onVmSizeChange={(vmSize) =>
            props.setWizardState((state) => ({ ...state, vmSize }))
          }
          onKeepOriginalVMSizeChange={(keepOriginalVMSize) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalVMSize,
            }))
          }
          onCompatibilityChange={onCompatibilityChange}
        />
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={onKeepOriginalTagsChange}
          onChange={onTagsChange}
        />
      </CardContent>
      <Divider />
      <CardContent className='px-[40px]'>
        <Stack
          className='group/startInstanceAfterRestore'
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography className='font-semibold' variant='body1'>
            Start instance after restore
          </Typography>
          <Stack direction='row' alignItems='center' gap='8px'>
            <Tooltip
              title={
                'When enabled, Eon will start the restored instance automatically. Turn off to leave it in a stopped state.'
              }
            >
              <Icon
                className='opacity-0 group-hover/startInstanceAfterRestore:opacity-100 transition-opacity'
                iconClass='material-symbols-help-outline-rounded'
              />
            </Tooltip>
            <Switch
              size='small'
              checked={props.wizardState.startInstanceAfterRestore}
              onChange={() => {
                props.setWizardState((state) => ({
                  ...state,
                  startInstanceAfterRestore: !state.startInstanceAfterRestore,
                }));
              }}
            />
          </Stack>
        </Stack>
      </CardContent>
    </StepContainer>
  );
};

const VmName = ({
  cloudAccountId,
  resourceGroup,
  vmName,
  onChange,
}: {
  cloudAccountId?: string;
  resourceGroup?: string;
  vmName: string;
  onChange: (value: string, isValid: boolean) => void;
}) => {
  const [value, setValue] = useState(vmName);
  const [error, setError] = useState('');
  const [debouncedValue, setDebouncedValue] = useState(vmName);
  const dal = useDAL();
  const { data: body, isLoading } = dal.restore.azureVm.useCheckNameExists(
    cloudAccountId,
    resourceGroup,
    debouncedValue
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  useEffect(() => {
    if (!isLoading && body?.exists) {
      setError('VM name already exists');
    } else {
      setError('');
    }
  }, [body, isLoading]);

  useEffect(() => {
    onChange(debouncedValue, !error && body?.exists === false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, debouncedValue, error]);

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>VM name</FormLabel>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={Boolean(error)}
        helperText={error}
        inputProps={{
          maxLength: 80,
        }}
        InputProps={{
          endAdornment: isLoading ? (
            <InputAdornment position='end'>
              <Loader variant='inline' />
            </InputAdornment>
          ) : !value ? undefined : body ? (
            body.exists ? (
              <InputAdornment position='end'>
                <i className='clarity-error-line text-[22px]' />
              </InputAdornment>
            ) : (
              <InputAdornment position='end'>
                <i className='material-symbols-check-circle-outline-rounded' />
              </InputAdornment>
            )
          ) : undefined,
        }}
      />
    </FormControl>
  );
};
